import React, { useState, useEffect } from "react";
import { useTheme } from "../ThemeContext.jsx";
import ColorIndicator from "./ColorIndicator.jsx";
import { getThemesByCategory } from "../ThemeRegistry";

import { MenuRoot, MenuButton, MenuList, MenuItem } from "@cadenza/ui";

import { FaPalette } from "react-icons/fa";

const getCssVariableValue = (variableName) => {
  return getComputedStyle(document.documentElement).getPropertyValue(
    variableName
  );
};

const createCustomTheme = (baseHue, isDark) => {
  return {
    name: "custom",
    displayName: "Custom",
    themeColor: `hsl(${baseHue}, 50%, ${isDark ? 7 : 90}%)`,
    themeSecondaryColor: `hsl(${baseHue}, 50%, ${isDark ? 10 : 80}%)`,
    primaryTextColor: `hsl(${baseHue}, 50%, ${isDark ? 90 : 10}%)`,
    frameBorderColor: `hsl(${baseHue}, 50%, ${isDark ? 10 : 90}%)`,
    attentionColor: `hsl(${baseHue}, 50%, ${isDark ? 20 : 80}%)`,
    accentColor: `hsl(${baseHue}, 50%, ${isDark ? 30 : 70}%)`,
    warningColor: `hsl(${baseHue}, 50%, ${isDark ? 40 : 60}%)`,
    category: "custom",
    isDark,
  };
};

const getThemeColors = (colorSets, colorTheme, customHue, isDark) => {
  // Special case for custom theme with customHue
  if (colorTheme === "custom" && customHue !== undefined) {
    return [createCustomTheme(customHue, isDark)];
  }

  // Find the themeCategories fuzzy matching on the colorTheme to theme.name
  if (!colorSets || !colorTheme) {
    console.warn("Color sets or color theme not provided.");
    return;
  }
  const themes = colorSets.mono.concat(
    colorSets.vs,
    colorSets.polychrome,
    colorSets.custom
  );
  const matchingThemes = themes.filter((t) => t.name.includes(colorTheme));

  if (!matchingThemes.length) {
    console.warn(`Theme "${colorTheme}" not found in color sets.`);
    // Create a default theme object that matches your expected structure
    return [
      {
        name: "default",
        displayName: "Default",
        themeColor: "#f7f7f7",
        themeSecondaryColor: "#e0e0e0",
        primaryTextColor: "#333333",
        frameBorderColor: "#999999",
        attentionColor: "#ff0000",
        accentColor: "#666666",
        warningColor: "#ffcc00",
        category: "mono",
        isDark: false,
      },
    ];
  }

  // Return the theme colors
  return matchingThemes;
};

const ThemeMenuItem = ({
  themeName,
  displayName,
  onClick,
  customHue,
  isDark,
  themeCategories,
}) => (
  <li
    className="menu-item"
    onClick={() => onClick(themeName)}
    role="menuitem"
    tabIndex={0}
    onKeyPress={(e) => {
      if (e.key === "Enter") onClick(themeName);
    }}
  >
    <div
      className="bar"
      title={`${displayName} theme`}
      aria-label={`${displayName} theme`}
    >
      <ColorIndicator
        title={displayName}
        color={getThemeColors(themeCategories, themeName, customHue, isDark)}
        isDark={isDark}
      />
    </div>
  </li>
);

// Default labels (overridable via props)
const DEFAULT_LABELS = {
  themeButton: "Theme",
  current: "Current",
  mono: "Mono Themes",
  polychrome: "Polychrome Themes",
  vs: "Approx. VS Themes",
};

const ThemePaletteSelector = ({
  labels,
  menuListClassName = "dropdown-left",
}) => {
  const L = { ...DEFAULT_LABELS, ...(labels || {}) };

  const [themeName, setThemeName] = useState("");

  const [themeCategories] = useState(getThemesByCategory());

  const { setColorTheme, isDark, setIsDark, currentTheme, customHue } =
    useTheme();

  useEffect(() => {
    setTimeout(() => {
      // Force redraw to happen last so theme change has time to take effect
      setThemeName(getCssVariableValue("--theme-name"));
    }, 0);
  }, [currentTheme]);

  useEffect(() => {
    setTimeout(() => {
      // Get theme name from computed CSS if possible
      const computedThemeName = getComputedStyle(document.documentElement)
        .getPropertyValue("--theme-name")
        .trim();
      setThemeName(computedThemeName || currentTheme);
    }, 0);
  }, [currentTheme]);

  const handleColorThemeChange = (newColorTheme) => {
    // Switch off mode
    if (newColorTheme.includes("only")) {
      if (newColorTheme.includes("dark")) {
        setIsDark(true);
      }
      if (newColorTheme.includes("light")) {
        setIsDark(false);
      }
    }
    setColorTheme(newColorTheme);
  };

  return (
    <MenuRoot>
      {(api) => (
        <div>
          <MenuButton
            api={api}
            aria-label={L.themeButton}
            title={L.themeButton}
            className="actionButton"
            showCaret={false}
          >
            <FaPalette className="cs-big-icon" />
          </MenuButton>
          <MenuList api={api} className={menuListClassName}>
            <MenuItem className="menu-item">
              <header>
                {L.current}: {themeName}{" "}
              </header>
            </MenuItem>

            <MenuItem className="menu-item">
              <header>{L.mono}</header>
            </MenuItem>

            <ThemeMenuItem
              themeName="grayscale"
              displayName="Grayscale"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="mono-red"
              displayName="Red"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="mono-green"
              displayName="Green"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="mono-blue"
              displayName="Blue"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="custom"
              displayName="Custom Mono"
              onClick={handleColorThemeChange}
              customHue={customHue}
              isDark={isDark}
              themeCategories={themeCategories}
            />
            <ThemeMenuItem
              themeName="only-dark-red"
              displayName="Astron"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="only-dark-green"
              displayName="Night Vision"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="only-dark-amber"
              displayName="Amber Monitor"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="forest"
              displayName="Forest"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <hr />
            <li className="menu-item">
              <header>{L.polychrome}</header>
            </li>
            <ThemeMenuItem
              themeName="cs"
              displayName="Colour Safe"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="tol-muted"
              displayName="Tol Muted"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="modern"
              displayName="Modern"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="only-dark-rust"
              displayName="Rust"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="ocean"
              displayName="Ocean"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="savannah"
              displayName="Savannah"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="autumn"
              displayName="Autumn"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <hr />
            <li className="menu-item">
              <header>{L.vs}</header>
            </li>
            <ThemeMenuItem
              themeName="only-vs-dark"
              displayName="VS Dark"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="only-vs-light"
              displayName="VS Light"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="only-dark-monokai"
              displayName="VS Monokai"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
            <ThemeMenuItem
              themeName="only-dark-vs-solarized"
              displayName="VS Solarized Dark"
              onClick={handleColorThemeChange}
              themeCategories={themeCategories}
              isDark={isDark}
            />
          </MenuList>
        </div>
      )}
    </MenuRoot>
  );
};

export default ThemePaletteSelector;
